import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigation } from "react-router";
import { cn } from "~/lib/cn";
import { getMemberNavActiveKey } from "~/lib/member-nav-region";
import { useLibrarySignal } from "~/store/library-signal";
import { AppLink } from "~/components/ui/AppLink";
import { LibSeal, useSealPresence } from "~/components/layout/LibrarySeal";
import { IconHome, IconLibrary, IconProfile } from "~/components/layout/nav-icons";

/**
 * Persistent member tab bar — T98 `.tabbar` (fixed bottom, <768 only).
 * Three destinations: Mi espacio · Biblioteca · Perfil. Active tab via
 * member-nav-region (aria-current="page" → top line + ink in CSS).
 */
const TABS = [
  { key: "dashboard", routeKey: "dashboard", labelKey: "tabbar.dashboard", Icon: IconHome },
  { key: "library", routeKey: "library", labelKey: "tabbar.library", Icon: IconLibrary },
  { key: "profile", routeKey: "profile", labelKey: "tabbar.profile", Icon: IconProfile },
] as const;

export function TabBar() {
  const { t } = useTranslation("nav");
  const { pathname } = useLocation();
  const navigation = useNavigation();
  const active = getMemberNavActiveKey(pathname);

  /**
   * T556 — which tab she is going TO, while the destination's loader runs.
   *
   * React Router keeps the old screen mounted for the whole wait, so without this the
   * only thing on screen after a tap is the tab she LEFT, still lit. Measured on the
   * seeded local app — no network, no cold start — a hop costs 400-1240 ms, and at 120 ms
   * there was no `aria-busy`, no spinner and no changed `aria-current` anywhere in the
   * document. The tap simply looked ignored.
   *
   * `getMemberNavActiveKey` is reused rather than a second pathname mapper: the pending
   * tab and the active tab must be decided the same way, or they can disagree.
   */
  const pendingKey =
    navigation.state !== "idle" && navigation.location
      ? getMemberNavActiveKey(navigation.location.pathname)
      : null;
  const pendingTechniques = useLibrarySignal();
  const { mounted: sealMounted, clearing: sealClearing } = useSealPresence(
    pendingTechniques.length > 0,
  );

  /**
   * T888 — the bar publishes its own height, measured (the T390 pattern for the header).
   *
   * The bar is fixed over the page's bottom edge, so without a reserve the browser counts the
   * whole viewport as visible: whatever focus or `scrollIntoView` brings up from below stops
   * flush with the bottom — behind the bar (WCAG 2.2 SC 2.4.11). `:root`'s
   * `scroll-padding-bottom` reads this; the measurement includes the safe-area padding and
   * reads 0 from 48em (768px at the default text size; T895) where the bar is `display: none`. Removed on unmount: a route that stands
   * the bar down must not keep its reserve.
   */
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        "--app-tabbar-height",
        `${Math.round(bar.getBoundingClientRect().height)}px`,
      );
    publish();
    // jsdom and pre-2020 engines lack it: one measurement stands, and the CSS fallback covers the rest.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    // border-box: the measurement includes the safe-area padding, which can change alone (iOS PWA ↔ browser).
    observer?.observe(bar, { box: "border-box" });
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--app-tabbar-height");
    };
  }, []);

  return (
    <nav ref={barRef} className="tabbar" aria-label={t("tabbar.label")}>
      {TABS.map(({ key, routeKey, labelKey, Icon }) => (
        <AppLink
          key={key}
          routeKey={routeKey}
          /* `prefetch="intent"` — on a phone React Router fires it on `touchstart`, so it
             buys the finger-down-to-finger-up window and costs nothing until she reaches
             for a tab. `"render"` would prefetch all three on every member page load:
             faster, but three extra Function invocations per view, and the T190 budget
             says that is a decision to take on a measurement rather than by default. */
          prefetch="intent"
          className={cn(
            "tab",
            key === "library" && sealClearing && "is-clearing",
            pendingKey === key && active !== key && "is-pending",
          )}
          /* `aria-current` stays honest: it means "this IS the page", and during the wait
             it is not yet. The pending state gets `aria-busy` instead — the tab announces
             that it is working, not that it has arrived. */
          aria-current={active === key ? "page" : undefined}
          aria-busy={pendingKey === key && active !== key ? true : undefined}
        >
          <span className="glyph">
            <Icon />
            {key === "library" && sealMounted ? <LibSeal clearing={sealClearing} /> : null}
          </span>
          <span className={key === "library" && sealMounted ? "lib-link-shimmer" : undefined}>
            {t(labelKey)}
          </span>
        </AppLink>
      ))}
    </nav>
  );
}
